import { useRef, useState } from "react";
import { ArrowLeftRight, ArrowRight, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TagListInput } from "@/components/itsm-config/tag-list-input";
import { MetadataMultiSelect } from "@/components/itsm-config/metadata-multi-select";
import { normalizeJiraStatusName } from "@/lib/itsm-config/general-mapping";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Option {
  label: string;
  value: string;
}

/** Direction-aware header: "Harmony {kind}  →  {Provider} {kind}". When
 *  `bidirectional`, uses a two-way arrow (mapping is used both ways). */
function DirectionHeaders({
  kind,
  providerLabel,
  keyWidth,
  bidirectional,
}: {
  kind: string;
  providerLabel?: string;
  keyWidth?: string;
  bidirectional?: boolean;
}) {
  const Arrow = bidirectional ? ArrowLeftRight : ArrowRight;
  return (
    <TableRow>
      <TableHead className={keyWidth}>Harmony {kind}</TableHead>
      <TableHead>
        <span className="flex items-center gap-1.5">
          <Arrow className="text-muted-foreground h-3.5 w-3.5" />
          {providerLabel ? `${providerLabel} ${kind}` : `External ${kind}`}
        </span>
      </TableHead>
      <TableHead className="w-10" />
    </TableRow>
  );
}

/** Harmony-side key cell: readable label + raw enum key as muted subtext. */
function HarmonyKeyCell({
  keyName,
  labels,
  className,
}: {
  keyName: string;
  labels?: Record<string, string>;
  className?: string;
}) {
  // eslint-disable-next-line security/detect-object-injection -- keyName is a fixed Harmony enum value
  const label = labels?.[keyName];
  return (
    <TableCell className={className}>
      <span className="text-sm">{label ?? keyName}</span>
      {label && (
        <span className="text-muted-foreground ml-2 font-mono text-xs">
          {keyName}
        </span>
      )}
    </TableCell>
  );
}

/**
 * Priority mapping: Harmony priority (fixed dropdown) -> single external
 * value. External value is a dropdown when provider options are loaded, else
 * a free-text input as a fallback.
 */
export function PriorityMappingEditor({
  value,
  onChange,
  harmonyOptions,
  harmonyLabels,
  providerLabel,
  externalOptions,
  bidirectional,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  harmonyOptions: readonly string[];
  harmonyLabels?: Record<string, string>;
  providerLabel?: string;
  externalOptions?: Option[];
  bidirectional?: boolean;
}) {
  const [newKey, setNewKey] = useState("");
  const entries = Object.entries(value);
  const unusedKeys = harmonyOptions.filter((k) => !(k in value));

  const setVal = (k: string, v: string) => { onChange({ ...value, [k]: v }); };
  const remove = (k: string) => {
    const { [k]: _omit, ...next } = value;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <DirectionHeaders
            kind="priority"
            providerLabel={providerLabel}
            bidirectional={bidirectional}
          />
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground text-center text-sm">
                No mappings
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([k, v]) => (
              <TableRow key={k}>
                <HarmonyKeyCell keyName={k} labels={harmonyLabels} />
                <TableCell>
                  {externalOptions && externalOptions.length > 0 ? (
                    <Select value={v} onValueChange={(nv) => { setVal(k, nv); }}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {externalOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={v}
                      onChange={(e) => { setVal(k, e.target.value); }}
                      className="h-8"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { remove(k); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {unusedKeys.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={newKey} onValueChange={setNewKey}>
            <SelectTrigger className="h-8 w-[200px]">
              <SelectValue placeholder="Add Harmony priority..." />
            </SelectTrigger>
            <SelectContent>
              {unusedKeys.map((k) => (
                <SelectItem key={k} value={k}>
                  {/* eslint-disable-next-line security/detect-object-injection -- k is a fixed Harmony enum value */}
                  {harmonyLabels?.[k] ?? k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={!newKey}
            onClick={() => {
              setVal(newKey, "");
              setNewKey("");
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Status mapping: Harmony status (fixed dropdown) -> LIST of external
 * statuses (tags). External statuses use CreatableMultiSelect so multiple
 * values are real list items, not comma-separated text.
 */
export function StatusMappingEditor({
  value,
  onChange,
  harmonyOptions,
  harmonyLabels,
  providerLabel,
  externalOptions,
  bidirectional,
}: {
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
  harmonyOptions: readonly string[];
  harmonyLabels?: Record<string, string>;
  providerLabel?: string;
  externalOptions?: Option[];
  bidirectional?: boolean;
}) {
  const [newKey, setNewKey] = useState("");
  const entries = Object.entries(value);
  const unusedKeys = harmonyOptions.filter((k) => !(k in value));

  const setVal = (k: string, v: string[]) => { onChange({ ...value, [k]: v }); };
  const remove = (k: string) => {
    const { [k]: _omit, ...next } = value;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <DirectionHeaders
            kind="status"
            providerLabel={providerLabel}
            keyWidth="w-[220px]"
            bidirectional={bidirectional}
          />
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground text-center text-sm">
                No mappings
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([k, v]) => (
              <TableRow key={k}>
                <HarmonyKeyCell
                  keyName={k}
                  labels={harmonyLabels}
                  className="align-top pt-3"
                />
                <TableCell>
                  {externalOptions ? (
                    <MetadataMultiSelect
                      options={externalOptions}
                      value={v}
                      onChange={(nv) => { setVal(k, nv); }}
                      placeholder="Add external status"
                    />
                  ) : (
                    <TagListInput
                      value={v}
                      onChange={(nv) => { setVal(k, nv); }}
                      placeholder="Add external status + Enter"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { remove(k); }}>
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {unusedKeys.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={newKey} onValueChange={setNewKey}>
            <SelectTrigger className="h-8 w-[200px]">
              <SelectValue placeholder="Add Harmony status..." />
            </SelectTrigger>
            <SelectContent>
              {unusedKeys.map((k) => (
                <SelectItem key={k} value={k}>
                  {/* eslint-disable-next-line security/detect-object-injection -- k is a fixed Harmony enum value */}
                  {harmonyLabels?.[k] ?? k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={!newKey}
            onClick={() => {
              setVal(newKey, []);
              setNewKey("");
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Inbound status table: Jira status name (free text, stored the way the
 * backend matches it) -> one Harmony status. The reverse of
 * StatusMappingEditor, for Jira's linked-only inbound table.
 */
export function InboundStatusMappingEditor({
  value,
  onChange,
  harmonyOptions,
  harmonyLabels,
  providerLabel,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  harmonyOptions: readonly string[];
  harmonyLabels?: Record<string, string>;
  providerLabel: string;
}) {
  const [newKey, setNewKey] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const keyInput = useRef<HTMLInputElement>(null);
  const entries = Object.entries(value);
  const normalizedKey = normalizeJiraStatusName(newKey);
  const duplicate = normalizedKey !== "" && normalizedKey in value;
  const canAdd = normalizedKey !== "" && newStatus !== "" && !duplicate;

  const setVal = (k: string, v: string) => { onChange({ ...value, [k]: v }); };
  const remove = (k: string) => {
    const { [k]: _omit, ...next } = value;
    onChange(next);
  };
  const add = () => {
    if (!canAdd) return;
    setVal(normalizedKey, newStatus);
    setNewKey("");
    setNewStatus("");
    // Add disables itself once the row is cleared; keep focus in the form.
    keyInput.current?.focus();
  };

  const statusSelect = (
    current: string,
    onSelect: (v: string) => void,
    label: string,
    className = "h-8",
  ) => (
    <Select value={current} onValueChange={onSelect}>
      <SelectTrigger className={className} aria-label={label}>
        <SelectValue placeholder="Harmony status..." />
      </SelectTrigger>
      <SelectContent>
        {harmonyOptions.map((s) => (
          <SelectItem key={s} value={s}>
            {/* eslint-disable-next-line security/detect-object-injection -- s is a fixed Harmony enum value */}
            {harmonyLabels?.[s] ?? s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">{providerLabel} status</TableHead>
            <TableHead>
              <span className="flex items-center gap-1.5">
                <ArrowRight className="text-muted-foreground h-3.5 w-3.5" />
                Harmony status
              </span>
            </TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground text-center text-sm">
                No mappings
              </TableCell>
            </TableRow>
          ) : (
            entries.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell className="font-mono text-xs break-all">{k}</TableCell>
                <TableCell>
                  {statusSelect(v, (nv) => { setVal(k, nv); }, `Harmony status for ${k}`)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Remove ${k}`}
                    onClick={() => { remove(k); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          ref={keyInput}
          value={newKey}
          onChange={(e) => { setNewKey(e.target.value); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={`${providerLabel} status name`}
          aria-label={`New ${providerLabel} status name`}
          className="h-8 w-[220px]"
        />
        {statusSelect(newStatus, setNewStatus, "New Harmony status", "h-8 w-[220px]")}
        <Button
          variant="outline"
          size="sm"
          disabled={!canAdd}
          onClick={add}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
      <p role="status" className="text-muted-foreground text-xs">
        {duplicate ? `"${normalizedKey}" is already mapped above.` : ""}
      </p>
    </div>
  );
}
